import type { Editor } from '@provision/rich-text-editor';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ConversationMessageDraft,
  isDraftNonEmpty,
  useConversationMessageDraft,
  useDeleteConversationMessageDraft,
} from 'src/graphql';
import {
  applyEditorContent,
  editorHasContent,
  readEditorDocJson,
} from 'src/pages/Conversations/Chat/InputField/editor-content';
import type { SuggestedReplyBannerState } from 'src/pages/Conversations/Chat/InputField/suggested-reply-banner';
import { useNotifications } from 'src/utils/hooks';

interface UseComposerSuggestedReplyParams {
  conversationId: string;
  isPrivate: boolean;
  editorRef: MutableRefObject<Editor | null>;
  lastAppliedRef: MutableRefObject<string>;
  personalDraftContent: string;
  isViewingScheduledMessage: boolean;
  editorIsEmpty: boolean;
}

export interface ComposerSuggestedReply {
  suggestedReply: ConversationMessageDraft | null;
  bannerState: SuggestedReplyBannerState;
  /** The editor holds the suggestion verbatim: nothing may persist or overwrite it. */
  isUneditedSuggestionInEditor: () => boolean;
  canAutoHydrate: () => boolean;
  hydrateIfEmpty: () => boolean;
  view: () => void;
  /** True when it wrote the editor, so the caller can refresh its content fingerprint. */
  dismiss: () => boolean;
  restorePersonalDraft: (() => void) | null;
  reset: () => void;
  onSent: () => void;
}

export function useComposerSuggestedReply({
  conversationId,
  isPrivate,
  editorRef,
  lastAppliedRef,
  personalDraftContent,
  isViewingScheduledMessage,
  editorIsEmpty,
}: UseComposerSuggestedReplyParams): ComposerSuggestedReply {
  const { allDrafts, isLoading } = useConversationMessageDraft();
  const { deleteConversationMessageDraft } = useDeleteConversationMessageDraft();
  const { showErrorAlert } = useNotifications();

  // the backend retires a superseded suggestion, so presence alone gates the banner
  const suggestedReply = useMemo(() => {
    const inScope = allDrafts.filter(
      (draft) =>
        !draft.userId &&
        !draft.isScheduled &&
        !draft.discardedAt &&
        draft.conversation?.id === conversationId &&
        Boolean(draft.isPrivate) === isPrivate,
    );

    return inScope.toSorted((a, b) => draftTimestamp(b) - draftTimestamp(a))[0] ?? null;
  }, [allDrafts, conversationId, isPrivate]);

  const suggestedReplyId = suggestedReply?.id ?? null;

  // every drafts-cache change rebuilds the row object, so no callback may close over it
  const suggestedReplyRef = useRef(suggestedReply);
  useEffect(() => {
    suggestedReplyRef.current = suggestedReply;
  }, [suggestedReply]);

  const [loadedSuggestionId, setLoadedSuggestionId] = useState<string | null>(null);
  const uneditedSnapshotRef = useRef<string | null>(null);
  const autoHydrateBlockedIdRef = useRef<string | null>(null);

  const isUneditedSuggestionInEditor = useCallback(() => {
    const snapshot = uneditedSnapshotRef.current;
    return snapshot !== null && readEditorDocJson(editorRef.current) === snapshot;
  }, [editorRef]);

  // a suggestion belongs to the scope it was loaded in: it must not follow the agent to another
  // one, where nothing guards it any more and a blur would save it as that scope's own draft
  useEffect(() => {
    if (isUneditedSuggestionInEditor()) {
      editorRef.current?.commands?.clearContent?.(true);
      lastAppliedRef.current = '';
    }

    setLoadedSuggestionId(null);
    uneditedSnapshotRef.current = null;
    autoHydrateBlockedIdRef.current = null;
  }, [
    conversationId,
    editorRef,
    isPrivate,
    isUneditedSuggestionInEditor,
    isViewingScheduledMessage,
    lastAppliedRef,
  ]);

  const applySuggestion = useCallback((): boolean => {
    const editor = editorRef.current;
    const suggestion = suggestedReplyRef.current;
    if (!editor || !suggestion?.message) return false;

    // the dedupe in applyEditorContent must compare against the editor, not an earlier load
    lastAppliedRef.current = '';
    applyEditorContent(editor, suggestion.message, lastAppliedRef);
    uneditedSnapshotRef.current = readEditorDocJson(editor);
    setLoadedSuggestionId(suggestion.id);
    return true;
  }, [editorRef, lastAppliedRef]);

  const clearSuggestionState = useCallback(() => {
    uneditedSnapshotRef.current = null;
    setLoadedSuggestionId(null);
  }, []);

  const deleteSuggestion = useCallback(() => {
    const suggestion = suggestedReplyRef.current;
    if (!suggestion) return;

    void deleteConversationMessageDraft(suggestion, { clearLocalDrafts: false }).catch(() =>
      showErrorAlert('Unable to remove the suggested reply. Please try again.'),
    );
  }, [deleteConversationMessageDraft, showErrorAlert]);

  const canAutoHydrate = useCallback((): boolean => {
    if (!suggestedReplyId || isLoading || isViewingScheduledMessage) return false;
    if (autoHydrateBlockedIdRef.current === suggestedReplyId) return false;

    return !isDraftNonEmpty(personalDraftContent);
  }, [isLoading, isViewingScheduledMessage, personalDraftContent, suggestedReplyId]);

  const hydrateIfEmpty = useCallback((): boolean => {
    const editor = editorRef.current;
    if (!editor || !canAutoHydrate() || editorHasContent(editor)) return false;

    return applySuggestion();
  }, [applySuggestion, canAutoHydrate, editorRef]);

  // a suggestion arriving over an empty composer may fill it; it may never clear it
  useEffect(() => {
    hydrateIfEmpty();
  }, [hydrateIfEmpty]);

  const view = useCallback(() => {
    autoHydrateBlockedIdRef.current = null;
    applySuggestion();
  }, [applySuggestion]);

  const dismiss = useCallback((): boolean => {
    if (!suggestedReplyId) return false;

    const isLoaded = loadedSuggestionId === suggestedReplyId;
    const takeEditorBack = isLoaded && isUneditedSuggestionInEditor();

    if (takeEditorBack) {
      if (isDraftNonEmpty(personalDraftContent)) {
        applyEditorContent(editorRef.current, personalDraftContent, lastAppliedRef);
      } else {
        editorRef.current?.commands?.clearContent?.(true);
        lastAppliedRef.current = '';
      }
    }

    if (isLoaded) clearSuggestionState();

    autoHydrateBlockedIdRef.current = suggestedReplyId;
    deleteSuggestion();

    return takeEditorBack;
  }, [
    clearSuggestionState,
    deleteSuggestion,
    editorRef,
    isUneditedSuggestionInEditor,
    lastAppliedRef,
    loadedSuggestionId,
    personalDraftContent,
    suggestedReplyId,
  ]);

  const reset = useCallback(() => {
    // the suggestion no longer owns the composer, so it must not refill it on its own
    autoHydrateBlockedIdRef.current = suggestedReplyRef.current?.id ?? null;
    clearSuggestionState();
  }, [clearSuggestionState]);

  const restorePersonalDraft = useCallback(() => {
    applyEditorContent(editorRef.current, personalDraftContent, lastAppliedRef);
    reset();
  }, [editorRef, lastAppliedRef, personalDraftContent, reset]);

  const onSent = useCallback(() => {
    // the discarder retires the row too; deleting now also keeps a late eviction from refilling
    deleteSuggestion();
    reset();
  }, [deleteSuggestion, reset]);

  const isSuggestionLoaded =
    Boolean(suggestedReplyId) && loadedSuggestionId === suggestedReplyId;

  return {
    suggestedReply,
    bannerState: isSuggestionLoaded && !editorIsEmpty ? 'loaded' : 'available',
    isUneditedSuggestionInEditor,
    canAutoHydrate,
    hydrateIfEmpty,
    view,
    dismiss,
    restorePersonalDraft:
      isSuggestionLoaded && isDraftNonEmpty(personalDraftContent)
        ? restorePersonalDraft
        : null,
    reset,
    onSent,
  };
}

function draftTimestamp(draft: ConversationMessageDraft): number {
  return Date.parse(draft.updatedAt ?? draft.createdAt ?? '') || 0;
}
